import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { PermissionsService, PermissionType } from '@qtek/libs/permissions';
import { POPOUT_WINDOW } from '@qtek/libs/popout';
import {
  AiChatActions,
  AppState,
  selectChatsWithPreview,
} from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { AiChat, POPOUT_MODAL_DATA } from '@qtek/shared/models';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AiChatComponent } from '../ai-chat/ai-chat.component';

const AI_CHAT_POPOUT_STORAGE_KEY = 'aiChatPopoutState';

interface AiChatPopoutState {
  chatId: string | null;
  windowId: string;
  startNew: boolean;
  lazySubscribe: boolean;
}

export interface AiChatPopoutData {
  chatId?: string | null;
  windowId?: string | null;
  startNew?: boolean;
  lazySubscribe?: boolean;
}

@Component({
  selector: 'qt-ai-chat-popout',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    TranslationCoreModule,
    AiChatComponent,
  ],
  templateUrl: './ai-chat-popout.component.html',
  styleUrls: ['./ai-chat-popout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatPopoutComponent implements OnInit, OnDestroy {
  readonly showChat = signal(false);
  readonly showHistory = signal(false);
  readonly selectedChatId = signal<string | null>(null);
  readonly chatHistory = signal<AiChat[]>([]);
  readonly historyVisibleCount = signal(10);
  readonly chatSessionKey = signal(this.createWindowId());
  readonly lazyChatSubscribe = signal(false);

  readonly visibleHistory = computed(() =>
    this.chatHistory().slice(0, this.historyVisibleCount())
  );
  readonly hasMoreHistory = computed(
    () => this.chatHistory().length > this.historyVisibleCount()
  );

  readonly editingChatId = signal<string | null>(null);
  readonly editingTitle = signal('');

  static readonly HISTORY_PAGE_SIZE = 10;

  private readonly destroy$ = new Subject<void>();
  private readonly chatFeature = 'ai-agent';
  private readonly permissions = inject(PermissionsService);
  private readonly store = inject(Store<AppState>);
  private readonly data = inject(POPOUT_MODAL_DATA, { optional: true }) as
    | AiChatPopoutData
    | undefined;
  private readonly popoutWindow = inject(POPOUT_WINDOW, { optional: true });
  private lastChatHistoryMysid: string | null = null;
  private initialApplied = false;
  /** Chat ID passed when opening popout; never clear this from the list subscription */
  private initialChatId: string | null = null;

  constructor() {
    if (this.data?.windowId) {
      this.chatSessionKey.set(this.data.windowId);
    }

    this.store
      .select(selectChatsWithPreview)
      .pipe(takeUntil(this.destroy$))
      .subscribe(chats => {
        const normalized = (chats ?? []).filter(
          (chat): chat is AiChat => !!chat?.id
        );
        this.chatHistory.set(normalized);

        const current = this.selectedChatId();
        if (
          current &&
          current !== this.initialChatId &&
          !normalized.some(chat => chat.id === current)
        ) {
          this.selectedChatId.set(null);
        }
      });

    this.permissions
      .isAllowed$(this.chatFeature, PermissionType.View)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isAllowed => {
        this.showChat.set(isAllowed);
        if (isAllowed) {
          this.requestChatHistoryIfNeeded();
          this.applyInitialSelection();
        }
      });

    if (this.data?.lazySubscribe) {
      this.lazyChatSubscribe.set(true);
    }
  }

  ngOnInit(): void {
    this.applyInitialSelection();
  }

  startNewChat(): void {
    if (!this.showChat()) return;
    this.showHistory.set(false);
    this.lazyChatSubscribe.set(true);
    this.chatSessionKey.set(this.createWindowId());
    this.selectedChatId.set(null);
    this.requestChatHistoryIfNeeded();
    this.persistPopoutState({
      chatId: null,
      windowId: this.chatSessionKey(),
      startNew: true,
      lazySubscribe: this.lazyChatSubscribe(),
    });
  }

  toggleHistory(): void {
    if (!this.showChat()) return;
    const next = !this.showHistory();
    this.showHistory.set(next);
    if (next) {
      this.historyVisibleCount.set(AiChatPopoutComponent.HISTORY_PAGE_SIZE);
    }
  }

  showMoreHistory(): void {
    const next =
      this.historyVisibleCount() + AiChatPopoutComponent.HISTORY_PAGE_SIZE;
    this.historyVisibleCount.set(Math.min(next, this.chatHistory().length));
  }

  startEditTitle(item: AiChat): void {
    this.editingChatId.set(item.id);
    this.editingTitle.set((item.title ?? '').trim());
  }

  onEditingTitleInput(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value ?? '';
    this.editingTitle.set(value);
  }

  saveEditTitle(): void {
    const id = this.editingChatId();
    const title = this.editingTitle().trim();
    this.editingChatId.set(null);
    this.editingTitle.set('');
    if (!id) return;
    const payload = { id, res: { title: title || undefined } };
    this.store.dispatch(AiChatActions.updateChat({ payload }));
  }

  cancelEditTitle(): void {
    this.editingChatId.set(null);
    this.editingTitle.set('');
  }

  selectChat(chatId: string): void {
    if (!this.showChat()) return;
    this.showHistory.set(false);
    this.lazyChatSubscribe.set(false);
    this.selectedChatId.set(chatId);
    this.chatSessionKey.set(this.createWindowId());

    const payload: Parameters<
      typeof AiChatActions.getChatEvents
    >[0]['payload'] = {
      mysid: `chatEvent_${chatId}`,
      prms: { chatId },
    };

    this.store.dispatch(AiChatActions.getChatEvents({ payload }));
    this.persistPopoutState({
      chatId,
      windowId: this.chatSessionKey(),
      startNew: false,
      lazySubscribe: this.lazyChatSubscribe(),
    });
  }

  closeWindow(): void {
    if (typeof window !== 'undefined') {
      window.close();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getChatIdFromUrl(): string | null {
    try {
      const w = this.popoutWindow;
      if (!w?.location?.search) return null;
      const params = new URLSearchParams(w.location.search);
      return params.get('chatId');
    } catch {
      return null;
    }
  }

  private applyInitialSelection(): void {
    if (this.initialApplied) return;
    this.initialApplied = true;

    const chatId = this.data?.chatId ?? this.getChatIdFromUrl() ?? null;

    if (chatId) {
      this.initialChatId = chatId;
      this.lazyChatSubscribe.set(false);
      this.selectedChatId.set(chatId);
      this.chatSessionKey.set(this.createWindowId());
      this.store.dispatch(
        AiChatActions.getChatEvents({
          payload: {
            mysid: `chatEvent_${chatId}`,
            prms: { chatId },
          },
        })
      );
      this.requestChatHistoryIfNeeded();
      return;
    }

    if (this.data?.startNew || this.data?.lazySubscribe) {
      this.lazyChatSubscribe.set(true);
      this.chatSessionKey.set(this.createWindowId());
      this.selectedChatId.set(null);
      this.requestChatHistoryIfNeeded();
    }
  }

  private requestChatHistoryIfNeeded(): void {
    const mysid = `chats_${this.chatSessionKey()}`;
    if (this.lastChatHistoryMysid === mysid) {
      return;
    }

    const payload: Parameters<typeof AiChatActions.getChats>[0]['payload'] = {
      mysid,
    };

    this.store.dispatch(AiChatActions.getChats({ payload }));
    this.lastChatHistoryMysid = mysid;
  }

  private createWindowId(): string {
    return `popout-ai-${Date.now().toString(16)}-${Math.random()
      .toString(16)
      .slice(2, 10)}`;
  }

  private persistPopoutState(state: AiChatPopoutState): void {
    if (typeof window === 'undefined') return;
    try {
      const opener = window.opener;
      if (!opener || opener.closed || !opener.sessionStorage) {
        return;
      }

      opener.sessionStorage.setItem(
        AI_CHAT_POPOUT_STORAGE_KEY,
        JSON.stringify(state)
      );

      opener.postMessage(
        {
          type: 'ai-chat-popout-state',
          state,
        },
        window.location.origin
      );
    } catch {
      // Ignore cross-window or storage errors.
    }
  }
}
